import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import HowToSteps, { HowToStep } from '@comp/learn/how-to-steps'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  stepsEyebrow: string
  stepsTitle: string
  stepsBody: string
  docLabel: string
  steps: HowToStep[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'How to add MFA to your VPN',
    metaDescription:
      'Add phishing-resistant Monosign MFA to Fortigate, Palo Alto, Pulse Secure, Ivanti, or OpenVPN via SAML or RADIUS. Four-step walkthrough with deep-link to docs.',
    heroEyebrow: 'How-to · MFA on VPN',
    heroTitle: ['MFA on the VPN.', 'SAML or RADIUS.'],
    heroBody:
      'Add Monosign MFA to Fortigate, Palo Alto, Pulse Secure, Ivanti, OpenVPN, and more, through SAML or RADIUS. Risk-aware policies fire only when something looks off, so users barely notice.',
    badges: ['SAML 2 or RADIUS', 'FIDO2, push, OTP', '~5 minutes to read'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'See VPN integrations',
    stepsEyebrow: 'Walkthrough',
    stepsTitle: 'Pick a protocol, point it at Monosign.',
    stepsBody:
      'The shape works for any RADIUS- or SAML-speaking VPN. Specific vendor pages live in the help center.',
    docLabel: 'Continue in the full docs',
    steps: [
      {
        title: 'Pick the protocol your VPN speaks',
        body: 'Most modern VPN gateways federate over SAML 2; legacy or appliance-style VPNs prefer RADIUS. Monosign supports both: Fortigate SSL VPN, Palo Alto, Pulse Secure, Ivanti Connect Secure, and OpenVPN are all covered.',
        hint: 'If your VPN supports both, choose SAML; it gives you adaptive MFA and risk policy at the IdP layer.',
      },
      {
        title: 'Add Monosign as the authentication target',
        body: 'In the VPN admin console, register Monosign as a SAML IdP or RADIUS server. Drop in the metadata or shared secret, and point the user-pool attribute at the user identifier Monosign provides.',
      },
      {
        title: 'Set the MFA policy in Monosign',
        body: 'In Monosign, create or extend a flow that requires a second factor (FIDO2, push, OTP) for the VPN application. Step-up policies can fire on impossible-travel, untrusted devices, or specific groups.',
        hint: 'For RADIUS clients, enable push-with-OTP-fallback; many RADIUS clients still need a numeric prompt as a safety net.',
      },
      {
        title: 'Pilot, then roll out',
        body: 'Test with a single user group. Confirm the VPN client opens the Monosign challenge correctly, MFA fires, and the session lands. Then expand to the rest of the workforce.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Multi-Factor Authentication', description: 'The MFA capability behind every VPN.', href: '/sign/mfa' },
      { label: 'Integrations catalog', description: 'Filter by RADIUS or SAML to find your VPN.', href: '/integrations' },
      { label: 'Zero Trust', description: 'Use case: removing flat-network VPN risk.', href: '/solutions/zero-trust' },
    ],
  },
  tr: {
    metaTitle: "VPN'inize MFA nasıl eklenir",
    metaDescription:
      "Fortigate, Palo Alto, Pulse Secure, Ivanti veya OpenVPN'e SAML ya da RADIUS üzerinden kimlik avına dirençli Monosign MFA ekleyin. Dört adımlık rehber ve doküman bağlantısı.",
    heroEyebrow: "Rehber · VPN'de MFA",
    heroTitle: ["VPN'de MFA.", 'SAML veya RADIUS.'],
    heroBody:
      'Fortigate, Palo Alto, Pulse Secure, Ivanti, OpenVPN ve daha fazlasına SAML veya RADIUS üzerinden Monosign MFA ekleyin. Riske duyarlı politikalar yalnızca bir şeyler ters göründüğünde devreye girer; kullanıcılar neredeyse fark etmez.',
    badges: ['SAML 2 veya RADIUS', 'FIDO2, push, OTP', '~5 dakikalık okuma'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'VPN entegrasyonlarını görün',
    stepsEyebrow: 'Adım adım',
    stepsTitle: "Protokolü seçin, Monosign'a yönlendirin.",
    stepsBody:
      'Bu akış RADIUS veya SAML konuşan her VPN için geçerlidir. Üreticiye özel sayfalar yardım merkezinde.',
    docLabel: 'Rehberin tamamına gidin',
    steps: [
      {
        title: "VPN'inizin konuştuğu protokolü seçin",
        body: "Modern VPN ağ geçitlerinin çoğu SAML 2 üzerinden federasyon kurar; eski veya cihaz tabanlı VPN'ler RADIUS'u tercih eder. Monosign her ikisini de destekler: Fortigate SSL VPN, Palo Alto, Pulse Secure, Ivanti Connect Secure ve OpenVPN kapsam dahilindedir.",
        hint: "VPN'iniz her ikisini de destekliyorsa SAML'ı seçin; IdP katmanında uyarlanabilir MFA ve risk politikası sağlar.",
      },
      {
        title: "Monosign'ı kimlik doğrulama hedefi olarak ekleyin",
        body: "VPN yönetim konsolunda Monosign'ı SAML IdP veya RADIUS sunucusu olarak kaydedin. Metadata dosyasını veya paylaşılan gizli anahtarı girin ve kullanıcı havuzu özniteliğini Monosign'ın sağladığı kullanıcı tanımlayıcısına yönlendirin.",
      },
      {
        title: "MFA politikasını Monosign'da tanımlayın",
        body: "Monosign'da, VPN uygulaması için ikinci faktör (FIDO2, push, OTP) gerektiren bir akış oluşturun veya mevcut bir akışı genişletin. Step-up politikaları imkânsız seyahat, güvenilmeyen cihazlar veya belirli gruplar için tetiklenebilir.",
        hint: "RADIUS istemcileri için OTP yedekli push'ı etkinleştirin; birçok RADIUS istemcisi güvenlik ağı olarak hâlâ sayısal bir girişe ihtiyaç duyar.",
      },
      {
        title: 'Önce pilot, sonra yaygınlaştırın',
        body: "Tek bir kullanıcı grubuyla test edin. VPN istemcisinin Monosign doğrulamasını doğru açtığını, MFA'nın tetiklendiğini ve oturumun kurulduğunu doğrulayın. Ardından çalışanların geri kalanına yayın.",
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Çok Faktörlü Kimlik Doğrulama', description: "Her VPN'in arkasındaki MFA yeteneği.", href: '/sign/mfa' },
      { label: 'Entegrasyon kataloğu', description: "VPN'inizi bulmak için RADIUS veya SAML'a göre filtreleyin.", href: '/integrations' },
      { label: 'Zero Trust', description: 'Kullanım senaryosu: düz ağ VPN riskini ortadan kaldırmak.', href: '/solutions/zero-trust' },
    ],
  },
}

type Props = { locale: string }

export default function MfaVpnPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn/mfa-on-vpn"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/integrations' }}
        />
        <HowToSteps
          eyebrow={c.stepsEyebrow}
          title={<>{c.stepsTitle}</>}
          body={c.stepsBody}
          steps={c.steps}
          schemaName={c.metaTitle}
          docHref="https://help.monofor.com/monosign/fortigate-ssl-vpn-saml-integration"
          docLabel={c.docLabel}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
